import React, { useMemo } from "react";
import styled from "styled-components";

// Helpers
import { formatMoney } from '../../../Helpers/Currency/Currency';
import { toGBDate } from "../../../Helpers/DataTypesGeneral/DateAndTime";

// New auth models
import type { AuthUser } from "../../../Models/DTOs/AuthUser";
import {SubscriptionDTO} from "../../../Models/DTOs/Subscription";

const SinglePlanInfoLayout = styled.section`
  padding: calc(3rem/16);
  border-radius: 0 calc(10rem/16) calc(10rem/16) calc(10rem/16);
  background: #191919ff;
  margin: 4rem 0 2rem 0;
  position: relative;

  .single-plan-info__inner {
    padding: 1rem;
    display: flex;
    align-items: center;
    flex-wrap: wrap;

    .single-plan-info__label {
      border-radius: calc(10rem/16) calc(10rem/16) 0 0;
      background: #191919ff;
      color: white;
      position: absolute;
      top: calc(-32rem/16);
      padding: calc(10rem/16) calc(16rem/16);
      font-family: "Rubik", Helvetica, sans-serif;
      text-align: center;
      font-weight: normal;
      left: 0;
      margin: 0;
      font-size: calc(14rem/16);
      height: auto;

      span {
        display: inline-block;
        min-width: calc(100rem/16);
        height: calc(12rem/16);
        margin: 0 0 0 calc(5rem/16);
      }
    }

    p {
      margin: 0 0 calc(8rem/16) 0;
      width: 100%;
      font-size: calc(14rem/16);
      display: flex;
      min-height: calc(28rem/16);
      justify-content: space-between;

      span {
        display: inline-block;
        padding: calc(7rem/16) calc(10rem/16);
        line-height: 1;
        height: calc(28rem/16);
        border-radius: calc(5em/16);
        font-size: calc(14rem/16);
        font-weight: bold;
        color: white;
        background: #303030ff;

        &.single-plan-info__span {
          margin: 0 0 0 calc(7rem/16);
        }
      }

      &:last-of-type {
        margin: 0;
      }
    }
  }
`;

type Props = {
    authUser?: AuthUser | null;
    sub?: SubscriptionDTO | null;
};

const SinglePlanInfo: React.FC<Props> = ({ authUser, sub }) => {
    const planLabel = useMemo(() => {
        if (!sub) {
            return (
                <span className="u-relative_hidden single-plan-info__loader"><span className="u-full_cover_absolute loader--side-to-side" /></span>
            );
        }
        return sub.PriceNickname;
    }, [sub]);

    const price = formatMoney(sub?.LastInvoiceTotalAmount, sub?.LastInvoiceCurrency ?? sub?.PriceCurrency);
    const intervalCount = Math.max(1, sub?.BillingIntervalCount ?? 1);
    const billingPeriod = sub?.BillingInterval
        ? `${intervalCount} ${sub.BillingInterval}${intervalCount === 1 ? "" : "s"}`
        : null;
    const periodEnd = toGBDate(sub?.CurrentPeriodEndUtc);

    return (
        <SinglePlanInfoLayout className="layout layout--single-plan-info">
            <div className={`single-plan-info__inner ${sub ? "is-not-loading" : "is-loading"}`}>
                <h4 className="single-plan-info__label">
                    Your Plan: {planLabel}
                </h4>

                {sub ? (
                    <>
                        <p>
                            Latest invoice:
                            <span className="single-plan-info__span">{price ?? "—"}</span>
                        </p>

                        {billingPeriod && <p>
                            Current billing period:
                            <span className="single-plan-info__span">{billingPeriod}</span>
                        </p>}

                        <p>
                            Current period ends:
                            <span className="single-plan-info__span">{periodEnd ?? "—"}</span>
                        </p>
                    </>
                ) : (
                    <>
                        <p className="u-relative_hidden single-plan-info__loader">
                            <span className="u-full_cover_absolute loader--side-to-side" />
                        </p>

                        <p className="u-relative_hidden single-plan-info__loader">
                            <span className="u-full_cover_absolute loader--side-to-side" />
                        </p>
                    </>
                )}
            </div>
        </SinglePlanInfoLayout>
    );
};

export default SinglePlanInfo;
